import React, { FC, ReactNode, memo, useCallback, useEffect, useMemo } from 'react';
import styles from './EnvelopesTable.module.scss'

import { useTranslation } from 'react-i18next';

import {
  Checkbox, getCheckboxCheckedValue,
  LibFixedType,
  LibScrollPositionType,
  Table
} from '@infologistics/frontend-libraries'

import { useAppDispatch, useAppSelector } from '@hooks/redux';

import {
  resetEnvelopes,
  setCurrentBoxOguid,
} from '@store/modules/envelopes';

import { EnvelopeItem } from '@views/envelopes';
import { getEnvelopeCheckboxKey, useCheckboxes } from '@hooks/envelopes';

import EnvelopesPagination from './EnvelopesPagination';
import EnvelopeListItem from './EnvelopeItem/EnvelopeListItem';

import { IEnvelopesTableProps as IProps } from '@views/envelopes/types';
import cn from 'classnames';
import {
  getEnvelopeViewFields,
  getEnvelopeViewMode,
} from '../envelopeViewSettings';
import { EnvelopeViewMode } from '@store/modules/user/types';
import { getEnvelopeListFields } from '../dynamicFields';
import { Instance } from '@const/consts';

const EnvelopesTable: FC<IProps> = ({
  envelopes,
  envelopeFields,
  availableLabels,
  boxOguid,
}) => {
  const { t } = useTranslation(['envelopes'])
  const {
    headCheckbox,
    setHeadCheckbox,
    checkedEnvelopes,
    selectedCheckbox
  } = useCheckboxes()

  const userSettings = useAppSelector(state => state.user.settings);
  const instanceRU = useAppSelector((state) => state.utils.instance) === Instance.RU;
  const { asideWidth, envelopeViewSettings } = userSettings;
  const direction = useAppSelector(state => state.envelopes.envelopes.direction);
  const currentUserFilterOguid = useAppSelector(state => state.envelopes.currentUserFilterOguid);
  const viewMode = useMemo(
    () => getEnvelopeViewMode(envelopeViewSettings, currentUserFilterOguid, boxOguid),
    [boxOguid, currentUserFilterOguid, envelopeViewSettings]
  );
  const viewFields = useMemo(
    () => viewMode === EnvelopeViewMode.LIST
      ? getEnvelopeListFields(envelopeFields, direction)
      : getEnvelopeViewFields(
          envelopeFields,
          direction,
          boxOguid,
          envelopeViewSettings,
          currentUserFilterOguid
        ),
    [boxOguid, currentUserFilterOguid, direction, envelopeFields, envelopeViewSettings, viewMode]
  );
  const knownFieldKeys = useMemo(
    () => envelopeFields.map(({ systemKey }) => systemKey),
    [envelopeFields]
  );

  const dispatch = useAppDispatch()

  const selectAllCheckbox = useCallback((): ReactNode =>
    <Checkbox
      checked={getCheckboxCheckedValue(headCheckbox ?? false)}
      name='checkAllEnvelopes'
      onChange={() => setHeadCheckbox(!headCheckbox)}
      value='selectAllEnvelopes'
    />,
  [headCheckbox, setHeadCheckbox])

  const tableHeadings = useMemo(() => [
    { class: styles.checkbox, content: selectAllCheckbox() },
    { class: styles.icons, content: '' },
    { content: t('envelopes:table:head:subject') },
    ...viewFields.map((field) => ({
      class: styles.dynamicField,
      content: field.name,
    })),
    { class: styles.status, content: t('envelopes:table:head:status') },
    { class: styles.lastChange, content: t('envelopes:table:head:lastChange') },
    { class: styles.actions, content: t('envelopes:table:head:actions') },
  ], [selectAllCheckbox, t, viewFields])

  const listHeadings = useMemo(() => [
    { class: styles.checkbox, content: selectAllCheckbox() },
    { class: styles.icons, content: '' },
    { content: t('envelopes:table:head:subject') },
    { class: styles.fieldsList, content: t('envelopes:table:head:fields') },
    { class: styles.status, content: t('envelopes:table:head:status') },
    { class: styles.lastChange, content: t('envelopes:table:head:lastChange') },
    { class: styles.actions, content: t('envelopes:table:head:actions') },
  ], [selectAllCheckbox, t])

  useEffect(() => {
    return () => {
      dispatch(resetEnvelopes())
    }
  }, [dispatch])

  useEffect(() => {
    dispatch(setCurrentBoxOguid(boxOguid));
  }, [boxOguid, dispatch]);

  return (
    <div className={cn('overflow-y-scroll relative scrollbar-content', styles.wrapper)}>
      <Table
        fixedType={LibFixedType.SCROLLING_TAB}
        headings={viewMode === EnvelopeViewMode.LIST ? listHeadings : tableHeadings}
        customScroll
        pagination={<EnvelopesPagination />}
        paginationClasses={instanceRU ? styles.paginationWithRules : undefined}
        scrollPosition={LibScrollPositionType.FIXED}
        scrollContentClasses='mb-8'
        isUpdateTableY
        recalcTrigger={asideWidth}
      >
        {
          envelopes.map((envelope, idx) => {
            const envelopeKey = getEnvelopeCheckboxKey(envelope);
            const props = {
              idx,
              key: envelopeKey,
              envelope,
              envelopeFields: viewFields,
              availableLabels,
              checkbox: checkedEnvelopes[envelopeKey] ?? false,
              selectedCheckbox,
              currentBoxOguid: envelope.box.oguid || boxOguid,
            };

            return viewMode === EnvelopeViewMode.LIST ? (
              <EnvelopeListItem
                {...props}
                knownFieldKeys={knownFieldKeys}
              />
            ) : (
              <EnvelopeItem {...props} />
            );
          })
        }
      </Table>
    </div>
  )
}

const EnvelopesTableMemo = memo(EnvelopesTable)

export default EnvelopesTableMemo
